import React, { useMemo } from 'react'
import { Route, Switch, useLocation, useHistory, Link } from 'react-router-dom'
import { Layout, Menu, Breadcrumb, Affix, Button, Drawer } from 'antd'
import useSWR from 'swr'
import {
	CompassOutlined,
	DollarCircleOutlined,
	SearchOutlined,
} from '@ant-design/icons'
import { ExploreExpensesPage, ExpenseDetailPage } from '../../pages'
import ExpensesListPage from '../../pages/expenses-list'
import { buildCacheKey } from '../../util'
import api from '../../api'
import { INewEntity } from '../../typings/entities/entity'
import { ExpensesQueryProvider, useWindowDimensions } from '../../hooks'
import SearchWidget from '../../components/search-widget'
import { newExpensesRoutes, INewExpenseRoute } from './routes'

const ExpensesRoutes: React.FC = () => {
	const location = useLocation<any>()
	const history = useHistory()
	const { x } = useWindowDimensions()
	const selectKey = useMemo(
		() => (location.pathname.includes('tabla') ? 'tabla' : 'explore'),
		[location.pathname]
	)

	const [isDrawerOpen, setIsDrawerOpen] = React.useState(false)
	const isRootRoute = location.pathname === '/gastos'
	const entityID = location.pathname.split('/')[2]
	const { data: entity } = useSWR<INewEntity>(
		isRootRoute
			? 'undefined'
			: buildCacheKey('expense', 'discrete', { documentID: entityID }),
		isRootRoute
			? undefined
			: async () => {
					try {
						const response =
							entityID === 'tabla'
								? null
								: await api.get(`/entities/${entityID}`)

						return response?.data.data
					} catch (error) {
						return undefined
					}
			  }
	)

	const handleDiscreteClick = (param: any) => {
		if (param.key === 'tabla' && !location.pathname.includes('tabla')) {
			history.push(`${location.pathname}/tabla`, location.state)
		}

		if (param.key !== 'tabla' && location.pathname.includes('tabla')) {
			const pathArray = location.pathname.split('/')
			const newURL = pathArray.slice(0, pathArray.length - 1).join('/')
			history.push(newURL, location.state)
		}
	}

	const getComponentToRender = (route: INewExpenseRoute) => {
		switch (route.component) {
			case 'aggregation':
				return <ExploreExpensesPage />

			case 'list':
				return <ExpensesListPage />

			case 'detail':
				return <ExpenseDetailPage />

			default:
				throw Error('Invalid route level')
		}
	}

	const buildFrom = useMemo(
		() =>
			entity
				? entity.ancestors.sort((a, b) => a.hierarchyIndex - b.hierarchyIndex)
				: [],

		[entity]
	)

	const breadcrumbs = buildFrom.map((entity) => {
		const url = `/gastos/${entity.id}`
		return (
			<Breadcrumb.Item key={url}>
				<Link
					to={url}
					onClick={(e: any) => {
						e.preventDefault()
						history.push(url)
					}}
					style={{
						cursor: 'pointer',
						color: '#0181b0',
					}}
				>
					{entity.name}
				</Link>
			</Breadcrumb.Item>
		)
	})

	return (
		<ExpensesQueryProvider>
			<Layout style={{ marginLeft: '2rem' }}>
				<Affix offsetTop={125} style={{ position: 'absolute', left: x - 100 }}>
					<Button
						type='primary'
						shape='circle'
						icon={<SearchOutlined />}
						onClick={(e) => {
							e.preventDefault()
							setIsDrawerOpen(true)
						}}
					/>
				</Affix>
				<Drawer
					visible={isDrawerOpen}
					onClose={() => setIsDrawerOpen(false)}
					mask={false}
				>
					<SearchWidget />
				</Drawer>
				<Breadcrumb style={{ marginBottom: '1rem', marginLeft: '1rem' }}>
					<Breadcrumb.Item key={'/gastos'}>
						<Link
							to={'/gastos'}
							style={{
								cursor: 'pointer',
								color: location.pathname === '/gastos' ? 'gray' : '#0181b0',
							}}
							onClick={(e) => {
								e.preventDefault()
								if (location.pathname === '/gastos') return
								history.push('/gastos')
							}}
						>
							Gastos
						</Link>
					</Breadcrumb.Item>
					{breadcrumbs}
					{entity && (
						<Breadcrumb.Item key={'/gastos'}>{entity.name}</Breadcrumb.Item>
					)}
				</Breadcrumb>
				<Layout.Header
					style={{
						width: '100%',
						background: 'white',
						marginBottom: '1rem',
						borderRadius: '5px',
						lineHeight: '4.5em',
					}}
				>
					<Menu mode='horizontal' selectedKeys={[selectKey]}>
						<Menu.Item
							key='explore'
							icon={<CompassOutlined />}
							onClick={handleDiscreteClick}
						>
							Navegar
						</Menu.Item>

						<Menu.Item
							key='tabla'
							icon={<DollarCircleOutlined />}
							onClick={handleDiscreteClick}
						>
							{entity
								? `Gastos de "${entity.name}"`
								: 'Gastos a Nivel Nacional'}
						</Menu.Item>
					</Menu>
				</Layout.Header>
				<Layout.Content>
					<Switch>
						{newExpensesRoutes.map((route) => (
							<Route path={route.path} key={route.path} exact>
								{getComponentToRender(route)}
							</Route>
						))}
					</Switch>
				</Layout.Content>
			</Layout>
		</ExpensesQueryProvider>
	)
}

export default ExpensesRoutes
